import { captureBrand, isValidBrandKey, parseBrandCookie } from './brand-key';

export interface Brand {
  key: string;
  // 10 digits, no formatting, e.g. 8889697667
  phone?: string;
  // Absolute URL of the partner logo, shown next to the CoverRight logo
  logo?: string;
  // Partner's b2b page, where the brand logo links to
  url?: string;
  // Partner-specific Calendly link (override or the partner agent's own link)
  calendly?: string;
  // Partner's agent id — new leads from this brand are assigned to them
  agentId?: string;
}

// Generic partners Calendly event, used for branded visitors without their own link
export const DEFAULT_PARTNER_CALENDLY = 'https://calendly.com/coverright-advisors/medicare-consultation-partners';

const CACHE_PREFIX = 'cr_brand_cache:';
// Short enough that Strapi edits (phone, logo) show up within minutes
const CACHE_TTL_MS = 10 * 60 * 1000;
const FETCH_TIMEOUT_MS = 3000;

let loadedBrand: Brand | null = null;

// Brand resolved by initBrand(); read synchronously during render
export function getLoadedBrand(): Brand | null {
  return loadedBrand;
}

// Call before the first render so the header never flashes the default phone.
// Resolves to null (default branding) when there is no brand or the API fails.
export async function initBrand(brandApiUrl: string): Promise<Brand | null> {
  const key = captureBrand();
  loadedBrand = key ? await fetchBrand(brandApiUrl, key) : null;
  return loadedBrand;
}

// SSR: resolve the brand from the raw cr_brand cookie value of the incoming request
export async function resolveServerBrand(brandApiUrl: string, cookieValue: string | undefined): Promise<Brand | null> {
  const key = parseBrandCookie(cookieValue)?.k;
  return key ? fetchBrand(brandApiUrl, key) : null;
}

export async function fetchBrand(brandApiUrl: string, key: string): Promise<Brand | null> {
  if (!isValidBrandKey(key) || !brandApiUrl) return null;

  const cached = readCache(key);
  if (cached !== undefined) return cached;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(`${brandApiUrl.replace(/\/$/, '')}/api/brand/${key}`, { signal: controller.signal });
    // Server errors aren't cached so the next page load retries
    if (!res.ok && res.status !== 404) return null;
    // Unknown keys (404) are cached too, so they don't cost a request on every page load
    const brand: Brand | null = res.ok ? { key, ...pickBrandFields(await res.json()) } : null;
    writeCache(key, brand);
    return brand;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

function pickBrandFields(json: any): Omit<Brand, 'key'> {
  return {
    phone: typeof json?.phone === 'string' && /^\d{10}$/.test(json.phone) ? json.phone : undefined,
    logo: isHttpUrl(json?.logo) ? json.logo : undefined,
    url: isHttpUrl(json?.url) ? json.url : undefined,
    calendly: isHttpUrl(json?.calendly) ? json.calendly : undefined,
    agentId: typeof json?.agentId === 'string' && /^[\w-]{1,64}$/.test(json.agentId) ? json.agentId : undefined,
  };
}

function isHttpUrl(value: unknown): value is string {
  return typeof value === 'string' && /^https?:\/\//.test(value);
}

function readCache(key: string): Brand | null | undefined {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return undefined;
    const { t, v } = JSON.parse(raw);
    return Date.now() - t < CACHE_TTL_MS ? v : undefined;
  } catch {
    return undefined;
  }
}

function writeCache(key: string, value: Brand | null) {
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ t: Date.now(), v: value }));
  } catch {
    // Storage unavailable (private mode, quota) — just skip caching
  }
}
